/**
 * quillEmailSetup — switch Quill's `size` and `font` formats from CLASS-based
 * (ql-size-huge / ql-font-*) to INLINE-STYLE based, and give them real whitelists
 * (px sizes + the full Google-font list).
 *
 * Why: class-based formats only render inside a `.ql-editor` (the class CSS is
 * scoped there). On the canvas preview and in the exported email — where the HTML
 * is dropped into a plain <div> — those classes do nothing, so "Huge" showed no
 * change. Inline styles (font-size:48px / font-family:'Inter',sans-serif) render
 * everywhere and are email-safe. Registration is global + idempotent
 * (Quill.register overwrites the default formats), so every RichTextEditor
 * instance in the app benefits.
 */
import Quill from 'quill'
import { FONT_FAMILIES, loadGoogleFont } from './blockDefaults'

// px sizes offered in the toolbar (Klaviyo-style numeric list, not small/large/huge).
export const SIZE_WHITELIST = [
  '10px', '11px', '12px', '13px', '14px', '16px', '18px', '20px',
  '24px', '28px', '32px', '36px', '40px', '48px', '56px', '64px',
]

// Toolbar options / data-values keep the FULL css value (proper quoting +
// fallback → the applied inline style is email-safe, e.g. font-family:'Inter',…).
export const FONT_WHITELIST = FONT_FAMILIES.map(f => f.value)

// CRITICAL: Parchment's canAdd() strips quotes from the value BEFORE testing it
// against the attributor whitelist. So the attributor's whitelist must hold the
// quote-STRIPPED form ("Inter, sans-serif"), or every quoted font silently fails
// to apply (sizes have no quotes, which is why only fonts were affected).
const FONT_STYLE_WHITELIST = FONT_FAMILIES.map(f => f.value.replace(/["']/g, ''))

let _registered = false

export function registerQuillEmailFormats() {
  if (_registered) return
  _registered = true

  const SizeStyle = Quill.import('attributors/style/size')
  SizeStyle.whitelist = SIZE_WHITELIST
  Quill.register(SizeStyle, true)

  const FontStyle = Quill.import('attributors/style/font')
  FontStyle.whitelist = FONT_STYLE_WHITELIST
  Quill.register(FontStyle, true)

  injectPickerLabels()
  // Load every Google font so the picker previews them and applied text renders.
  FONT_FAMILIES.filter(f => f.google).forEach(f => loadGoogleFont(f.name))
}

// Quill sets each option's data-value to the raw css value. Escape embedded
// double-quotes so the CSS attribute selector is valid; single quotes pass through.
const cssAttr = (v) => v.replace(/"/g, '\\"')

// Quill's snow CSS only labels the built-in small/large/huge + a few fonts. Our
// custom px sizes and full font list would otherwise render as BLANK dropdown
// items, so we inject a ::before label for every whitelisted value.
function injectPickerLabels() {
  if (document.getElementById('ql-email-picker-labels')) return

  // Font options → show the clean NAME, previewed in its own typeface.
  const fontRules = FONT_FAMILIES.map(f => {
    const dv = cssAttr(f.value)
    const item = `.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="${dv}"]`
    const label = `.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="${dv}"]`
    return `${item}::before, ${label}::before { content: "${f.name}"; font-family: ${f.value}; }`
  }).join('\n')

  // Size options → show the numeric px value (e.g. "14").
  const sizeRules = SIZE_WHITELIST.map(px => {
    const num = px.replace('px', '')
    const item = `.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="${px}"]`
    const label = `.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="${px}"]`
    return `${item}::before, ${label}::before { content: "${num}"; }`
  }).join('\n')

  // Long font list must scroll instead of running off-screen; give the size
  // dropdown a modest cap too. Roomy toolbar so controls aren't cramped.
  const ergonomics = `
    .ql-snow .ql-picker.ql-font .ql-picker-options {
      max-height: 320px; overflow-y: auto; overflow-x: hidden;
      min-width: 180px; padding: 6px 0;
      box-shadow: 0 8px 24px rgba(0,0,0,0.12); border-radius: 8px;
    }
    .ql-snow .ql-picker.ql-size .ql-picker-options {
      max-height: 320px; overflow-y: auto; padding: 6px 0;
      box-shadow: 0 8px 24px rgba(0,0,0,0.12); border-radius: 8px;
    }
    .ql-snow .ql-picker.ql-font .ql-picker-item,
    .ql-snow .ql-picker.ql-size .ql-picker-item { padding: 5px 12px; font-size: 13px; }
  `

  const style = document.createElement('style')
  style.id = 'ql-email-picker-labels'
  style.textContent = `${fontRules}\n${sizeRules}\n${ergonomics}`
  document.head.appendChild(style)
}
